import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, MapPinned } from "lucide-react";
import { search_addresses, resolve_place } from "@/lib/geocoding";

// Buscador de direcciones con sugerencias (geocodificación real, Colombia).
// - Conserva SIEMPRE el texto completo que escribió el usuario (incl. números y letras);
//   la selección solo aporta coordenadas/ciudad, nunca reemplaza el texto por el nombre de la calle.
// - Clasifica coincidencias: exacta (con número), aproximada (solo calle) o zona.
// - Ofrece "Ubicar la casa en el mapa" cuando el número no está registrado en OSM.

const KIND_META = {
  house: { label: "Exacta", cls: "border-emerald-500/60 bg-emerald-500/15 text-emerald-400" },
  street: { label: "Aproximada · calle", cls: "border-amber-500/60 bg-amber-500/15 text-amber-400" },
  area: { label: "Zona aproximada", cls: "border-gray-500/60 bg-gray-500/15 text-gray-400" },
};

export const AddressSearch = ({ icon: Icon = MapPin, label, placeholder, accent = "#FF6A00", onSelect, testId }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [pickedLabel, setPickedLabel] = useState(null);
  const [picked, setPicked] = useState(null); // {label, kind} | {manual:true}
  const boxRef = useRef(null);
  const timer = useRef(null);

  // cerrar al hacer clic fuera
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // debounce de búsqueda + cancelación de consultas obsoletas (AbortController)
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const text = q.trim();
    if (text.length < 4 || text === pickedLabel) {
      setOpen(false);
      setResults([]);
      setSearched(false);
      return;
    }
    const controller = new AbortController();
    timer.current = setTimeout(async () => {
      setLoading(true);
      const r = await search_addresses(text, controller.signal);
      if (controller.signal.aborted) return;
      setLoading(false);
      if (r.aborted) return;
      if (r.ok) {
        setResults(r.results);
        setSearched(true);
        setOpen(true);
      }
    }, 600);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      controller.abort();
    };
  }, [q, pickedLabel]);

  const pick = async (item) => {
    // NO reemplazar el texto escrito: solo fijar coordenadas
    const typed = q.trim();
    setPickedLabel(typed);
    setOpen(false);
    let resolved = item;
    // Predicciones de Google no traen coordenadas: se resuelven UNA vez al seleccionar
    if (item.needsResolution && item.placeId) {
      setLoading(true);
      const r = await resolve_place(item.placeId);
      setLoading(false);
      if (r.ok) {
        resolved = { ...item, ...r };
      } else {
        setPicked({ label: item.label, kind: item.kind });
        return; // no se pudieron obtener coordenadas; el usuario puede reintentar o usar el pin manual
      }
    }
    setPicked({ label: resolved.label || item.label, kind: resolved.kind });
    onSelect?.({ ...resolved, typed });
  };

  const pickManual = () => {
    setPickedLabel(q.trim());
    setPicked({ manual: true });
    setOpen(false);
    onSelect?.({ manual: true, typed: q.trim() });
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      {label && (
        <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
          <Icon size={13} style={{ color: accent }} /> {label}
        </label>
      )}
      <div className="relative">
        <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: accent }} />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPickedLabel(null);
            setPicked(null);
          }}
          onFocus={() => results.length > 0 && q.trim() !== pickedLabel && setOpen(true)}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          autoComplete="off"
          className="w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] py-3 pl-10 pr-9 text-sm text-white placeholder-gray-500 outline-none transition-colors focus:border-[#FF6A00]"
        />
        {loading && (
          <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-500" />
        )}
      </div>

      {/* Qué se encontró realmente para el texto escrito (sin fingir casa exacta) */}
      {picked && !picked.manual && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500" data-testid={`${testId}-match-caption`}>
          <span>Coincidencia: <span className="text-gray-300">{picked.label}</span></span>
          {KIND_META[picked.kind] && (
            <span className={`rounded-full border px-2 py-0.5 font-bold uppercase tracking-wide ${KIND_META[picked.kind].cls}`}>
              {KIND_META[picked.kind].label}
            </span>
          )}
        </div>
      )}
      {picked?.manual && (
        <div className="mt-1.5 text-[11px] text-gray-500" data-testid={`${testId}-match-caption`}>
          Ubicación manual: fija y confirma el pin en el mapa.
        </div>
      )}

      {open && (
        <div className="absolute inset-x-0 top-full z-[1100] mt-1.5 overflow-hidden rounded-xl border border-[#27272A] bg-[#141416] shadow-2xl">
          {results.length === 0 && searched && !loading && (
            <div className="px-4 py-3.5 text-sm text-amber-300" data-testid={`${testId}-no-results`}>
              No encontramos esa dirección. El número puede no estar registrado en el mapa; usa «Ubicar la casa en el mapa».
            </div>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(r)}
              data-testid={`${testId}-suggestion-${i}`}
              className="flex w-full items-start gap-2.5 border-b border-[#1e1e21] px-4 py-3 text-left text-sm text-gray-200 transition-colors hover:bg-[#1e1e21]"
            >
              <MapPin size={14} className="mt-0.5 shrink-0 text-gray-500" />
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 block">{r.label}</span>
                {KIND_META[r.kind] && (
                  <span className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${KIND_META[r.kind].cls}`}>
                    {KIND_META[r.kind].label}
                  </span>
                )}
              </span>
            </button>
          ))}
          {/* Siempre disponible: ubicar manualmente conservando el texto escrito */}
          <button
            type="button"
            onClick={pickManual}
            data-testid={`${testId}-manual-pin`}
            className="flex w-full items-center gap-2.5 border-t border-[#FF6A00]/30 bg-[#FF6A00]/10 px-4 py-3 text-left text-sm font-semibold text-[#FF6A00] transition-colors hover:bg-[#FF6A00]/20"
          >
            <MapPinned size={15} className="shrink-0" />
            Ubicar la casa en el mapa
            <span className="ml-auto text-[10px] font-normal text-gray-400">conserva tu dirección</span>
          </button>
        </div>
      )}
    </div>
  );
};
